import createClient, { type Middleware } from 'openapi-fetch'

import { auth } from '../auth/auth'
import type { paths } from './schema'

/** One problem with one input, as reported by the API (``errors[]`` of the error body). */
export interface FieldIssue {
  code: string
  message: string
  /** Path of the input, e.g. ``name`` or ``budgetEntries[1].amount`` */
  field?: string
}

/**
 * Error with a message that is safe to show (German, from the API). ``code`` is the stable
 * identifier of the case (``measure.not_found``, ``version.stale``, ``request.invalid`` …);
 * ``requestId`` identifies the request in the server logs.
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly issues: FieldIssue[]
  readonly requestId?: string
  readonly traceId?: string

  constructor(
    status: number,
    message: string,
    options: { code?: string; issues?: FieldIssue[]; requestId?: string; traceId?: string } = {},
  ) {
    super(message)
    this.status = status
    this.code = options.code ?? `http.${status}`
    this.issues = options.issues ?? []
    this.requestId = options.requestId
    this.traceId = options.traceId
  }

  /** Someone else saved a newer version in the meantime (optimistic locking). */
  get isConflict(): boolean {
    return this.code === 'version.stale'
  }

  /** The server failed (not the input): worth reporting with the request ID. */
  get isServerError(): boolean {
    return this.status >= 500
  }

  /** Message for one input, e.g. ``fieldError('name')`` – for showing API errors at the field. */
  fieldError(field: string): string | undefined {
    return this.issues.find((issue) => issue.field === field)?.message
  }
}

export const NETWORK_ERROR_MESSAGE =
  'Keine Verbindung zum Server. Bitte die Netzwerkverbindung prüfen und erneut versuchen.'

// --- municipality of all requests --------------------------------------------------------

let currentTenantId: string | null = null

/** The municipality every API request works in (header X-Tenant-Id). */
export function setCurrentTenant(id: string | null): void {
  currentTenantId = id
}

export function getCurrentTenant(): string | null {
  return currentTenantId
}

async function headers(): Promise<Record<string, string>> {
  const result: Record<string, string> = {}
  const token = await auth().token()
  if (token) result.Authorization = `Bearer ${token}`
  if (currentTenantId) result['X-Tenant-Id'] = currentTenantId
  return result
}

const contextMiddleware: Middleware = {
  async onRequest({ request }) {
    for (const [name, value] of Object.entries(await headers())) request.headers.set(name, value)
    return request
  },
}

/** fetch that turns "server unreachable" into an ApiError instead of a raw "Failed to fetch". */
async function guardedFetch(input: Request): Promise<Response> {
  try {
    return await fetch(input)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new ApiError(0, NETWORK_ERROR_MESSAGE, { code: 'network.unreachable' })
  }
}

export const api = createClient<paths>({ baseUrl: '', fetch: guardedFetch })
api.use(contextMiddleware)

interface ErrorBody {
  code?: string
  message?: string
  errors?: FieldIssue[]
  requestId?: string
  traceId?: string
}

function fallbackMessage(status: number): string {
  if (status === 401) return 'Bitte melden Sie sich erneut an.'
  if (status === 403) return 'Für diese Aktion fehlt die Berechtigung.'
  if (status === 428) return 'Bitte wählen Sie eine Kommune aus.'
  if (status === 502 || status === 503 || status === 504)
    return 'Der Server ist gerade nicht erreichbar. Bitte in einem Moment erneut versuchen.'
  return `Die Anfrage ist fehlgeschlagen (Status ${status}). Bitte später erneut versuchen.`
}

/** Builds the ApiError from an error response body (see ErrorOut in the API). */
export function apiErrorOf(status: number, body: unknown, response?: Response): ApiError {
  const b = (body && typeof body === 'object' ? body : {}) as ErrorBody
  return new ApiError(status, b.message || fallbackMessage(status), {
    code: b.code,
    issues: Array.isArray(b.errors) ? b.errors : [],
    requestId: b.requestId ?? response?.headers.get('X-Request-ID') ?? undefined,
    traceId: b.traceId,
  })
}

/** Unwrap an openapi-fetch result: return data or throw an ApiError. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error !== undefined || !result.response.ok) {
    throw apiErrorOf(result.response.status, result.error, result.response)
  }
  return result.data as T
}

/** Download a file from the API (with auth and municipality) and hand it to the browser. */
export async function download(path: string, fallbackName: string): Promise<void> {
  let response: Response
  try {
    response = await fetch(path, { headers: await headers() })
  } catch {
    throw new ApiError(0, NETWORK_ERROR_MESSAGE, { code: 'network.unreachable' })
  }
  if (!response.ok) {
    // the error body names the concrete case (e.g. an unknown filter value)
    const body: unknown = await response.json().catch(() => undefined)
    throw apiErrorOf(response.status, body, response)
  }
  const disposition = response.headers.get('Content-Disposition') ?? ''
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName
  const url = URL.createObjectURL(await response.blob())
  const link = document.createElement('a')
  link.href = url
  link.download = name
  link.click()
  URL.revokeObjectURL(url)
}
